<script lang="ts">
    import { ParticipantAvatarFragmentDoc, type ParticipantInput } from '$gql/graphql';
    import {
        participantToInput,
        participantInputAreEqual,
    } from '$lib/utils/participantUtils';
    import { useFragment } from '$gql';
    import ParticipantAvatar from '$components/ParticipantAvatar/ParticipantAvatar.svelte';
    import * as t from '$trans';
    import { createDropdownMenu, melt } from '@melt-ui/svelte';
    import { fly } from 'svelte/transition';
    import { defaultButtonClass } from '$components/Feed/FeedPostForm/Button.svelte';
    import { cx } from '$lib/utils/cx';
    import { getContainer } from '$components/Context/ContainerContext';

    export let portal: HTMLElement | null = null;
    export let onChange: (p: ParticipantInput) => void;
    export let onFocus: () => void = () => {};
    export let onBlur: () => void = () => {};
    export let title: string | null = null;
    export let only: ParticipantInput[] = [];
    let className = '';
    export { className as class };

    export let value: ParticipantInput;

    const { currentUserStore, lang } = getContainer();
    const currentUser = currentUserStore().currentUser();

    const {
        elements: { trigger, menu, item },
        states: { open },
    } = createDropdownMenu({
        positioning: {
            placement: 'bottom-start',
            sameWidth: false,
            gutter: 4,
        },
        forceVisible: true,
        loop: true,
        portal,
    });

    $: participants = (
        $currentUser == null
            ? []
            : [$currentUser, ...$currentUser.managedCompanies.map((m) => m.company)]
    ).filter(
        (p) =>
            only.length == 0 ||
            only.find((e) => participantInputAreEqual(participantToInput(p), e)),
    );

    $: selectedParticipant =
        participants
            .filter((p) => participantInputAreEqual(value, participantToInput(p)))
            .at(0) ?? null;
</script>

{#if participants.length > 1}
    <button
        use:melt={$trigger}
        type="button"
        class={cx(defaultButtonClass, 'b:p-0 b:pr-[6px] b:gap-[0]', className)}
        on:focus={() => onFocus()}
        on:blur={() => onBlur()}
    >
        {#if selectedParticipant != null}
            <ParticipantAvatar
                dontWrapInLink
                participant={useFragment(
                    ParticipantAvatarFragmentDoc,
                    selectedParticipant,
                )}
                size={40}
            />
        {/if}
        <i
            class="tnp tnp-chevron-down text-12 text-gray-650"
            aria-label={t.ui.select_participant({}, $lang)}
        />
    </button>

    {#if $open}
        <div
            class="dropdown-menu-leo"
            {...$menu}
            use:menu
            transition:fly={{ duration: 150, y: -10 }}
        >
            {#if parent != null}
                <div class="dropdown-menu-leo__header">
                    <button
                        class="dropdown-menu-leo__back-button"
                        type="button"
                        aria-label={t.ui.back_to_return({}, $lang)}
                        on:click|preventDefault={() => {
                            $open = false;
                        }}
                        ><i class="text-16 tnp tnp-chevron-left" aria-hidden={true} />
                    </button>
                    <span class="grow"
                        >{title ?? t.feed_post.publish_from({}, $lang)}</span
                    >
                </div>
            {/if}

            {#each participants as p}
                <div
                    class="dropdown-menu-leo__item"
                    on:m-click={() => {
                        onChange(participantToInput(p));
                    }}
                    {...$item}
                    use:item
                >
                    <input
                        type="radio"
                        class="m-0"
                        checked={participantInputAreEqual(participantToInput(p), value)}
                    />
                    <span class="grow font-medium text-left">{p.participantName}</span>
                    <ParticipantAvatar
                        dontWrapInLink
                        participant={useFragment(ParticipantAvatarFragmentDoc, p)}
                        size={32}
                    />
                </div>
            {/each}
        </div>
    {/if}
{:else if selectedParticipant != null}
    <ParticipantAvatar
        dontWrapInLink
        participant={useFragment(ParticipantAvatarFragmentDoc, selectedParticipant)}
        size={40}
    />
{/if}
